import axios from "axios";
import * as bcrypt from "bcryptjs";
import * as ApiSettingService from "../../db/serviceApiSetting";
import { toast } from "react-toastify";
import * as lc from "../../app/modules/localstorage/index";
import { env } from "../../config/env";

export interface IExportOrderResponse {
  error?: string;
  message?: string;
  warning?: string;
  response?: string;
}

export type WarehouseExportRequest = {
  account: string;
  warehouse: string[];
  fileType: string;
  from_time: string;
  to_time: string;
  status: string[];
  urlFile: string;
};

export type OrderExportRequest =
  WarehouseExportRequest;

/**
 * Send export order request to the server
 * @param jsonRequestData Request data to send
 * @returns Response data or throws an error
 */
export const sendExportOrder = async (
  jsonRequestData: OrderExportRequest
): Promise<{ statusCode: number; data: IExportOrderResponse }> => {
  const url: string = env.SERVER_URL + "/v2/orders/export";

  const accountAccess = await ApiSettingService.getAccess();
  if (!accountAccess?.accessToken) {
    return {
      statusCode: 401,
      data: {
        error: "UNAUTHORIZED",
        message: "Authentication token is missing",
      },
    };
  }
  const token = accountAccess.accessToken;

  console.log("URL sendExportOrder: " + url);

  try {
    const response = await axios.post(url, jsonRequestData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 15000, // 10 seconds timeout
    });

    return {
      statusCode: response?.status,
      data: response?.data,
    };
  } catch (err: any) {
    console.log("Error Request server: ", err);

    if (axios.isAxiosError(err)) {
      if (err?.response) {
        return {
          statusCode: err.response.status,
          data: err.response.data,
        };
      } else if (err?.request) {
        return {
          statusCode: 503,
          data: {
            message: "Service Unavailable. No response received from server.",
          },
        };
      }
    }

    return {
      statusCode: 500,
      data: {
        message: err?.message || "Unexpected error occurred.",
      },
    };
  }
};

const dummyData = {
  message: "Success",
  processId: "123123123123",
  data: [
    {
      accountName: "LazadaSZ",
      count: 2,
    },
    {
      accountName: "ShopeeFS",
      count: 4,
    },
  ],
};

export const reqReadyToShipOrder = async (
  orders: string[],
  userID?: string,
  processID?: string
) => {
  const url: string = env.SERVER_URL + "/7days/orders/rts";
  try {
    const token = await bcrypt.hash(env.SERVER_TOKEN, 10);
    const reqBody: {
      orders: string[];
      userID?: string;
      processID?: string;
    } = {
      orders: orders,
    };

    if (userID) {
      reqBody.userID = userID;
    }
    if (processID) {
      reqBody.processID = processID;
    }
    console.log("SET_READY_TO_SHIP", { orders });
    const response = await axios.post(url, reqBody, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (err: any) {
    console.error("Error Request server:", err);
    if (axios.isAxiosError(err)) {
      // Handle axios-specific errors
      if (err.response) {
        // Client received an error response (5xx, 4xx)
        toast(
          `Error: ${err.response.status}, Message: ${err.message}, Please try again later`,
          {
            type: "error",
            position: "top-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else if (err.request) {
        // Client never received a response, or request never left
        toast(`Network error, can't send a request, Please try again later`, {
          type: "error",
          position: "top-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        // Anything else
        toast(`Error: ${err.message}, Please try again later`, {
          type: "error",
          position: "top-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } else {
      // Handle non-axios-specific errors
      toast(`Unexpected Error: ${err.message}, Please try again later`, {
        type: "error",
        position: "top-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }

    return undefined;
  }
};

export const reqDownloadCSVExportedOrders = async (
  orders: string[],
  userID: string,
  processID?: string
) => {
  const url: string = env.SERVER_URL + "/v2/orders/download";
  const reqBody: any = {
    orders: orders,
    userID: userID,
  };

  if (processID) {
    reqBody["processID"] = processID;
  }

  try {
    // get latest access token from database
    const accountAccess = await ApiSettingService.getAccess();
    if (!accountAccess?.accessToken) {
      console.error("Failed To Get Access Token");
      return undefined;
    }
    const token = accountAccess.accessToken;

    const response = await axios.post(url, reqBody, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    console.log("Response From Download Exported Order", response.data);
    return response.data;
  } catch (err) {
    console.error("Error Request server : " + err);
    return undefined;
  }
};

export type syncOrderProps = {
  startTime?: string;
  endTime?: string;
  accounts?: string[];
};

export const syncOrder = async (
  syncOrderProps: syncOrderProps,
  client: string
) => {
  if (!syncOrderProps) {
    console.log("Invalid syncOrderProps");
    return undefined;
  }
  const url: string = env.SERVER_URL + "/v2/orders/sync-orders";
  console.log("Start Time:", syncOrderProps.startTime);
  console.log("End Time:", syncOrderProps.endTime);
  console.log("Account IDs:", syncOrderProps.accounts);
  console.log("Client ID:", client);
  console.log("URL: " + url);
  try {
    const accountAccess = await ApiSettingService.getAccess();
    if (!accountAccess?.accessToken) {
      console.error("Failed To Get Access Token");
      return undefined;
    }
    const token = accountAccess.accessToken;

    const requestBody: {
      client: string;
      accounts?: string[];
      startTime?: string;
      endTime?: string;
    } = {
      client: client,
      accounts: syncOrderProps.accounts,
      startTime: syncOrderProps.startTime,
      endTime: syncOrderProps.endTime,
    };

    const response = await axios.post(url, requestBody, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    console.log("Response Status:", response.status, "ulti");
    console.log("Response Data:", response.data);
    return response.data;
  } catch (error: any) {
    console.log("Error post to server:", error, "ulti");

    if (error.response) {
      console.log("Error response status:", error.response.status);
      console.log("Error response data:", error.response.data);
    } else if (error.request) {
      console.log("No response received from the server");
    } else {
      console.log("Error message:", error.message);
    }

    return undefined;
  }
};

export const sendRequestInitiateChatMarketplace = async (
  marketplaceType: string,
  orderId: string,
  userId: string
) => {
  if (marketplaceType === 'tokopedia') {
    throw new Error('MARKETPLACE_NOT_SUPPORTED');
  }

  console.log("==> Masuk sendRequestInitiateChatMarketplace INCHA");
  console.log(marketplaceType, "Marketplacetype INCHA");
  console.log(orderId, "orderId INCHA");
  console.log(userId, "userId INCHA");
  const url: string =
    env.SERVER_URL +
    `/v2/chat/${marketplaceType}/initiate`;
  console.log(url, "url INCHA");
  const requestBody = {
    orderId: orderId,
    userId: userId,
  };
  console.log(requestBody, "requestBody INCHA");
  // get latest access token from database
  const accountAccess = await ApiSettingService.getAccess();
  if (!accountAccess) {
    console.error("Failed To Get Access Token");
    return undefined;
  }
  const token = accountAccess?.accessToken;
  console.log(token, "token INCHA");
  const response = await axios.post(url, requestBody, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  console.log(response, "response INCHA");

  return response.data;
};

export const sendRequestShippingMark = async (
  packingStations?: Array<string>,
  qtyCount?: number,
  orderSN?: any,
  shipper?: string,
  handoverId?: string
) => {
  const url = `${env.SERVER_URL}/orders/shippingMark`;
  const token = env.CLIENT_TOKEN;

  if (!packingStations && qtyCount === undefined && !orderSN) {
    throw new Error(
      "At least one of packingStation, qtyCount, or orderSN is required."
    );
  }
  console.log(handoverId, " ini handover id");
  const requestBody = {
    packingStations,
    qtyCount,
    orderSN,
    shipper,
    handoverId,
  };

  try {
    const response = await axios.post(url, requestBody, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 400) {
      console.error(
        "Error sending request for shipping mark:",
        error.response.data
      );
      throw new Error(`${error.response.data.message}`);
    } else if (error instanceof Error) {
      console.error("Error sending request for shipping mark:", error);
      throw new Error(`Failed to send shipping mark: ${error.message}`);
    } else {
      console.error("Unexpected error:", error);
      throw new Error(
        "Failed to send shipping mark request: An unexpected error occurred."
      );
    }
  }
};

export const sendRequestShippingHandover = async (
  driverName?: string,
  vehicleNumber?: string,
  packageScannedQty?: number,
  succesScannedQty?: number,
  failedScannedQty?: number,
  canceledPackage?: number,
  date?: string,
  time?: string,
  shipper?: string,
  description?: string,
  handoverId?: string
) => {
  const url = `${env.SERVER_URL}/orders/shipmentHandOver`;
  const token = env.CLIENT_TOKEN;

  if (!shipper) {
    throw new Error("Shipper is required.");
  }

  console.log(handoverId, "ini handover id");
  // Retrieve client information from local storage
  const client = lc.getItemLC(lc.LCName.Client);

  if (!client || !client.id) {
    throw new Error("Client information is missing or invalid.");
  }

  const requestBody = {
    driverName,
    vehicleNumber,
    packageScannedQty,
    succesScannedQty,
    failedScannedQty,
    canceledPackage,
    date,
    time,
    shipper,
    description,
    client,
    handoverId,
  };

  console.log("REQUEST TO SERVER SHIPPING HANDOVER", requestBody);

  try {
    const response = await axios.post(url, requestBody, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    console.log("Response from shipping handover:", response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // Handle axios-specific errors
      const errorMessage = error.response
        ? `Error: ${error.response.status}, ${
            error.response.data.message || error.message
          }`
        : `Network error: ${error.message}`;

      console.error(
        "Error sending request for shipping handover:",
        errorMessage
      );
      throw new Error(
        `Failed to send shipping handover request: ${errorMessage}`
      );
    } else {
      // Handle non-axios-specific errors
      console.error("Unexpected error:", error);
      throw new Error(
        "Failed to send shipping handover request: An unexpected error occurred."
      );
    }
  }
};

export const sendRequestForwardOrder = async (status: string) => {
  if (!status) {
    throw new Error("Status is required.");
  }

  const url = `${env.SERVER_URL}/playground/forward-${
    status === "NEW_ORDER"
      ? "new-order"
      : status === "READY_TO_SHIP"
      ? "rts-order"
      : ""
  }`;

  try {
    const response = await axios.get(url);

    if (response && response?.status !== 200) {
      return {
        statusCode: response.status,
        error: {
          code: "SERVER_ERROR",
          message: response.data.message || "An error occurred",
        },
      };
    }

    return {
      statusCode: 200,
      message: response.data.message,
      data: response.data,
    };
  } catch (error: any) {
    if (error?.response && error?.response?.status === 400) {
      console.error(
        "Error sending request for Forward Order",
        error?.response?.data
      );
      return {
        statusCode: 400,
        error: {
          code: error?.response?.data?.code || "BAD_REQUEST",
          message: error?.response?.data?.message || "Bad request",
        },
      };
    }

    return {
      statusCode: 500,
      error: {
        code: error?.code || "SERVER_ERROR",
        message: error?.message || "An unexpected error occurred",
      },
    };
  }
};

export const sendRequestRTSOrder = async (
  orders: string[],
  userID?: string,
  processID?: string
) => {
  if (!orders || (orders && orders?.length < 1)) {
    throw new Error("Order is required.");
  }

  const url: string = env.SERVER_URL + "/7days/orders/rts";

  try {
    const token = await bcrypt.hash(env.SERVER_TOKEN, 10);
    const reqBody: {
      orders: string[];
      userID?: string;
      processID?: string;
    } = {
      orders: orders,
    };

    if (userID) {
      reqBody.userID = userID;
    }
    if (processID) {
      reqBody.processID = processID;
    }
    const response = await axios.post(url, reqBody, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response && response?.status !== 200) {
      return {
        statusCode: response.status,
        error: {
          code: "SERVER_ERROR",
          message: response.data.message || "An error occurred",
        },
      };
    }

    return {
      statusCode: 200,
      message: response.data.message,
      data: response.data,
    };
  } catch (err: any) {
    console.error("Error Request server:", err);
    if (axios.isAxiosError(err)) {
      // Handle axios-specific errors
      if (err.response) {
        // Client received an error response (5xx, 4xx)
        toast(
          `Error: ${err.response.status}, Message: ${err.message}, Please try again later`,
          {
            type: "error",
            position: "top-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else if (err.request) {
        // Client never received a response, or request never left
        toast(`Network error, can't send a request, Please try again later`, {
          type: "error",
          position: "top-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        // Anything else
        toast(`Error: ${err.message}, Please try again later`, {
          type: "error",
          position: "top-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } else {
      // Handle non-axios-specific errors
      toast(`Unexpected Error: ${err.message}, Please try again later`, {
        type: "error",
        position: "top-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }

    return undefined;
  }
};
